import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import {
  type IResponseImportNumbersStatus,
  type IResponseIncomingTwilioNumbers,
} from 'entities/Phone/api/types'
import { PhoneApi } from 'entities/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { BillingApi, PriceInfoStore } from 'entities/Billing'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import { ImportTwilioNumbersUi } from 'pages/settings/pages/numbers/ui/ImportTwilioNumbers/ui/ImportTwilioNumbersUi'
import { ManageNumbersStore } from 'pages/settings/pages/numbers/ui/ManageNumbers'

export class ImportTwilioNumbersStore {
  private _priceInfoStore: PriceInfoStore
  private _importNumbersStatus: IResponseImportNumbersStatus | null = null
  private _importProcessLoading = false
  private _importNumbersError = false
  private _abortController = new AbortController()

  constructor(
    public numbers: IResponseIncomingTwilioNumbers['data'],
    private _entities: IEntitiesStore
  ) {
    this._priceInfoStore = new PriceInfoStore((count, cancelToken) =>
      BillingApi.getBillingPreview(
        {
          plan: this._entities.subscriptionStore.plan?.shared_inboxes_plan_id || '',
          numbers: Number.isNaN(count) ? 1 : count,
          use_unassigned_numbers: 0,
          use_bought_numbers: 0,
        },
        {
          cancelToken: cancelToken,
        }
      )
    )
    this.init()
    makeAutoObservable(this)
  }
  licenseGracePeriodModalId = nanoid()
  importTwilioNumbersModalId = nanoid()

  private init = () => {
    if (this.isTwilioNumberVendor) {
      modalStore.addModal({
        id: this.importTwilioNumbersModalId,
        title: 'Import Twilio numbers',
        pureContent: true,
        ModalContent: () => <ImportTwilioNumbersUi store={this} />,
      })
    } else {
      this.getImportNumbersStatus()
    }
  }

  dispose = () => {
    this._abortController.abort()
    this._priceInfoStore.reset()
  }

  getImportNumbersStatus = async () => {
    try {
      const { data } = await PhoneApi.getImportNumbersStatus()
      this._importNumbersStatus = data
      this._entities.organizationStore.updateOrganizationNumbersGracePeriod(
        data.numbers_grace_period.is_active
      )

      if (data.available_numbers_licenses === 0) {
        this.initPriceData(this.numbersCount)

        return
      }
      if (this.isLessLicensesThanNumbers && this.numbersLicensesNumber) {
        const needed = this.numbersCount - this.numbersLicensesNumber
        this.initPriceData(needed)

        return
      }

      uiStore.changeRoute({ path: `/${NumbersPath.importTwilioNumbers.root}` })
    } catch (e) {
      logger.error(e)
    }
  }

  initPriceData = (count: number) => {
    this._priceInfoStore.loadPriceData(count)

    uiStore.changeRoute({ path: `/${NumbersPath.importTwilioNumbers.root}` })
  }

  onCloseLicenseGracePeriodModal = () => {
    modalStore.closeModal(this.licenseGracePeriodModalId)
  }

  onClose = () => {
    modalStore.closeModal(this.importTwilioNumbersModalId)
  }

  handleNext = async () => {
    this._importProcessLoading = true
    await this.onNext()
    this._importProcessLoading = false
  }

  handleAddNumber = async () => {
    if (this.isEnoughLicenses) {
      await this.handleNext()

      return
    }

    modalStore.addModal({
      id: this.licenseGracePeriodModalId,
      type: ModalTypeList.WARNING,
      title: 'License grace period starts after numbers are imported',
      desc: `You will have ${this.gracePeriod} hours to assign or remove your numbers. After that, you’ll only be charged for any unpaid licenses.`,
      pureContent: true,
      width: 280,
      primaryAction: {
        text: 'Confirm import',
        onAction: () => {
          this.handleNext()
          this.onCloseLicenseGracePeriodModal()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          this.onCloseLicenseGracePeriodModal()
        },
      },
    })
  }

  onNextByTwilioIntegration = async () => {
    try {
      await PhoneApi.importProcess()
      this.onClose()
      toastStore.add({
        type: 'info',
        title: 'Import in progress',
        desc: 'Your data is being processed. We’ll notify you once it’s complete.',
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        const data = e.response?.data
        if (data?.needs_numbers_licenses) {
          const manageNumbersStore = new ManageNumbersStore(
            data.needs_numbers_licenses,
            this.onNext,
            this._entities
          )
          manageNumbersStore.onManageNumbers({ type: 'default' })
          this.onClose()
        } else {
          console.error(e)
        }
      } else {
        console.error(e)
      }
    }
  }

  onNextByTwilio = async () => {
    try {
      await PhoneApi.importProcess()
      toastStore.add({
        type: 'success',
        title: 'Numbers imported successfully',
      })
      await this._entities.subscriptionStore.init()
      await this.getImportNumbersStatus()
      uiStore.changeRouteViaVue({
        path: `/${NumbersPath.root}?tab=unassigned`,
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this._importNumbersError = true
      } else {
        logger.error(e)
      }
    }
  }

  onNext = async () => {
    if (this.isTwilioNumberVendor) {
      await this.onNextByTwilioIntegration()
    } else {
      await this.onNextByTwilio()
    }
  }

  get numbersCount() {
    return this.numbers.length
  }

  get priceInfoStore() {
    return this._priceInfoStore
  }

  get isEnoughLicenses() {
    return this.numbersLicensesNumber >= this.numbersCount
  }

  get isLessLicensesThanNumbers() {
    if (!this._importNumbersStatus || this.numbersLicensesNumber === 0) return false

    return this.numbersLicensesNumber < this.numbersCount
  }

  get numbersLicensesNumber() {
    return this._importNumbersStatus?.available_numbers_licenses ?? 0
  }

  get gracePeriod() {
    return this._importNumbersStatus?.numbers_grace_period.duration_hours ?? 0
  }

  get importProcessLoading() {
    return this._importProcessLoading
  }

  get hasImportNumbersError() {
    return this._importNumbersError
  }

  get isTwilioNumberVendor() {
    return this._entities.organizationStore.isTwilioNumberVendor
  }
}
